import { definedMap } from "@glideapps/ts-necessities";

import { SerializedComponent } from "../../components/SerializedApp";
import { getDocURL } from "../../docUrl";
import { makeText, setColumnInRowForEditComponent } from "../builder-utils";
import {
    ComponentDescription,
    ComponentKind,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    getTableColumn,
    getTableName,
} from "../description";
import { getPropertyWithColumnFormat } from "../formulas";
import {
    makeSubtitlePropertyDescriptor,
    makeSummaryImagePropertyDescriptor,
    makeTitlePropertyDescriptor,
} from "./descriptor-utils";
import { ComponentHandlerBase } from "./handler";
import { getColumnProperty } from "./property-utils";
import { ComponentBuilder, ComponentDescriptor, WireInflationBackend, WireRowComponentHydrator } from ".";
import { asString } from "../../computation-model/data";
import { WireComponentKind } from "../../wire/component-kind";

export const ComponentKindUserProfile: ComponentKind = "user-profile";

export interface UserProfileComponentDescription extends ComponentDescription {
    readonly titleProperty: PropertyDescription | undefined;
    readonly subtitleProperty: PropertyDescription | undefined;
    readonly imageURLProperty: PropertyDescription | undefined;
}

export class UserProfileComponentHandler extends ComponentHandlerBase<UserProfileComponentDescription> {
    constructor() {
        super(ComponentKindUserProfile);
    }

    public get isGlideManaged(): boolean {
        return true;
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: UserProfileComponentDescription,
        { input, output }: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): SerializedComponent {
        const { b } = builder;

        function property(name: string) {
            return getPropertyWithColumnFormat(builder, input, name);
        }

        const imageURLProperty = getColumnProperty(desc.imageURLProperty);
        if (imageURLProperty === undefined) {
            builder.error("URL property for image is not a column");
            return b.emptyUI;
        }

        const column = definedMap(imageURLProperty, p => getTableColumn(input, p));
        if (column === undefined) {
            builder.error(`Column not found in table ${getTableName(input).name} for URL image`);
            return b.emptyUI;
        }
        const setColumnsResult = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            imageURLProperty,
            b.getProperty(imageURLProperty, b.getContext, output),
            undefined,
            output,
            undefined,
            b.getContext,
            { debounce: false }
        );
        if (setColumnsResult === undefined) return b.emptyUI;
        const { lets, valueToModify, action, isEnabled } = setColumnsResult;

        const subtitle = makeText(builder, desc.subtitleProperty, input, true, true, true);

        return b.let(
            lets,
            b.userProfile(
                definedMap(getColumnProperty(desc.titleProperty), property),
                subtitle,
                valueToModify,
                action,
                isEnabled,
                false
            )
        );
    }

    // Describes to glide what the configurator in the builder looks like
    public getDescriptor(): ComponentDescriptor<UserProfileComponentDescription> {
        const titleDescriptor = makeTitlePropertyDescriptor(undefined, true, false);
        // FIXME: This should include the case for the special value.
        const subtitleDescriptor = makeSubtitlePropertyDescriptor(undefined);
        const imageDescriptor = makeSummaryImagePropertyDescriptor(undefined, true, true, false);

        return {
            name: "User Profile",
            helpUrl: getDocURL("userProfile"), // TODO: Change this
            description: "A Test User Profile",
            img: "componentTitle",
            group: "Text",
            isLegacy: true,
            properties: [titleDescriptor, subtitleDescriptor, imageDescriptor, ...this.getBasePropertyDescriptors()],
        };
    }

    public inflate(
        ib: WireInflationBackend,
        desc: UserProfileComponentDescription
    ): WireRowComponentHydrator | undefined {
        const [titleGetter] = ib.getValueGetterForProperty(desc.titleProperty, true);
        const [subtitleGetter] = ib.getValueGetterForProperty(desc.subtitleProperty, true);
        const [imageGetter] = ib.getValueGetterForProperty(desc.imageURLProperty, true);

        return sc => {
            return {
                kind: WireComponentKind.UserProfile,
                title: definedMap(titleGetter(sc), asString),
                subtitle: definedMap(subtitleGetter(sc), asString),
                image: definedMap(imageGetter(sc), asString),
            };
        };
    }
}
